import {useEffect, useState} from 'react'
import styled from 'styled-components'
import IndexContentContainer from '../../index/IndexContentContainer'
import BookPageBar from '../../../common/BookPageBar'
import RowSpacer from '../../../common/styles/RowSpacer'
import BookReadingSessionButton from '../reading/BookReadingSessionButton'
import {BooksitoutServer} from '../../../config/BooksitoutServer'
import toast from 'react-hot-toast'
import {useNavigate, useParams} from 'react-router-dom'
import useBookDetailStore from './useBookDetailStore'
import BookRatingModal from '../BookRatingModal'
import Center from '../../../common/Center'
import BookRating from '../../../common/BookRating'
import BookDoneModal from './BookDoneModal'
import useReadingSessionStore from '../reading/useReadingSessionStore'
import BooksitoutImages from '../../../images/BooksitoutImages'
import {Author} from '../../../common/Author'
import BookDetailMenuButton from './BookDetailMenuButton'
import BookGiveUpButton from '../BookGiveUpButton'
import BookEditModal from '../edit/BookEditModal'

const BookDetailInfoCard = () => {
    const navigate = useNavigate()

    const {bookId} = useParams()
    const {book, updateBookStatus} = useBookDetailStore()
    const {playConfetti} = useReadingSessionStore()

    const [rating, setRating] = useState<number>(0)
    useEffect(() => {
        if (book != undefined) {
            setRating(book?.rating ?? 0)
        }
    }, [book])

    const [isRatingModalOpen, setIsRatingModalOpen] = useState<boolean>(false)
    const openRatingModal = e => {
        e.preventDefault()
        setIsRatingModalOpen(true)
    }

    const onDelete = () => {
        if (!window.confirm('책을 삭제할까요?')) {
            return
        }

        BooksitoutServer.delete(`/v1/book/${book!.id}`)
            .then(() => {
                toast.success('책이 삭제됐어요')
                navigate(`/book/mine?range=${book!.status === 'GIVE_UP' ? 'GIVEUP' : book!.status === 'DONE' ? 'DONE' : 'READING'}`)
            })
            .catch(e => toast.error(`책을 삭제할 수 없었어요. 잠시 후 다시 시도해 주세요 (${e})`))
    }

    const [isDoneModalOpen, setIsDoneModalOpen] = useState<boolean>(false)
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)

    if (book == null) {
        return <></>
    }

    const updateStatusToDone = (year: number, month: number, day: number) => {
        BooksitoutServer.put(`/v1/book/${book.id}`, {
            status: 'DONE',
            doneDate: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        })
            .then(() => {
                playConfetti()
                updateBookStatus('DONE')
                toast.success('책을 다 읽었어요! 축하 드려요! 👏')
            })
            .catch(() => toast.error(`오류가 났어요. 잠시 후 다시 시도해 주세요.`))
    }

    const onGiveUp = () => {
        const confirmationMessage = book.isGiveUp ? '책을 다시 읽을까요?' : '책을 포기할까요?'
        if (!window.confirm(confirmationMessage)) return

        if (book.isGiveUp) {
            BooksitoutServer.put(`/v1/book/${book.id}/un-give-up`)
                .then(() => {
                    toast.success('책을 다시 읽을게요.')
                    navigate(`/book/mine?range=READING`)
                })
                .catch(() => toast.error(`오류가 났어요. 잠시 후 다시 시도해 주세요.`))
        } else {
            BooksitoutServer.put(`/v1/book/${book.id}/give-up`)
                .then(() => {
                    toast.success('책을 포기했어요.')
                    navigate(`/book/mine?range=GIVEUP`)
                })
                .catch(() => toast.error(`오류가 났어요. 잠시 후 다시 시도해 주세요.`))
        }
    }

    const onMarkDone = () => {
        if (book.status === 'DONE') {
            if (!window.confirm('책을 읽고 있음으로 바꿀까요?')) return
            BooksitoutServer.put(`/v1/book/${book.id}`, {status: 'READING'})
                .then(() => {
                    updateBookStatus('READING')
                    toast.success('책을 "다시 읽고 있음"으로 바꿨어요.')
                })
                .catch(() => toast.error(`오류가 났어요. 잠시 후 다시 시도해 주세요.`))
        } else {
            setIsDoneModalOpen(true)
        }
    }

    const menuItems = [
        {label: '수정하기', onClick: () => setIsEditModalOpen(true)},
        ...(book.status !== 'DONE' ? [{label: '다 읽음으로', onClick: onMarkDone}] : []),
        ...(book.status === 'DONE' ? [{label: '다시 읽기', onClick: onMarkDone}] : []),
        {label: book.isGiveUp ? '다시 읽기' : '포기하기', onClick: onGiveUp},
        {label: '삭제하기', onClick: onDelete, danger: true},
    ]

    return (
        <IndexContentContainer isNotHover={true}>
            <BookRatingModal isModalOpen={isRatingModalOpen} onClose={() => setIsRatingModalOpen(false)}
                             updateRating={rating => setRating(rating)} book={book}/>

            <BookDoneModal isModalOpen={isDoneModalOpen} onClose={() => setIsDoneModalOpen(false)}
                           updateStatusToDone={(year, month, day) => updateStatusToDone(year, month, day)}/>

            <BookEditModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}/>

            <MenuContainer>
                <BookDetailMenuButton items={menuItems}/>
            </MenuContainer>

            <Container href={`${book.isbn == null ? `` : `/book/${book.isbn}?q=${book.title}`}`}>
                <CoverContainer>
                    <Cover
                        src={book.cover !== '' && book.cover != null ? book.cover : BooksitoutImages.Placeholder.bookCover}
                        alt={book.title}/>
                </CoverContainer>

                <InfoContainer>
                    <div className="d-block d-md-none">
                        <RowSpacer/>
                    </div>

                    <Title>{book.title}</Title>
                    <Author>{book.author}</Author>

                    <RowSpacer/>

                    <BookPageContainer>
                        <BookPageBar book={book}/>
                    </BookPageContainer>

                    <RowSpacer/>

                    {book.status == 'GIVE_UP' ? (
                        <Center>
                            <BookGiveUpButton book={book}/>
                        </Center>
                    ) : book.status != 'DONE' ? (
                        <ButtonContainerContainer>
                            <ButtonContainer>
                                <BookReadingSessionButton book={book}/>
                            </ButtonContainer>
                        </ButtonContainerContainer>
                    ) : (
                        <div onClick={openRatingModal}>
                            <BookRating rating={rating} size={40}/>
                        </div>
                    )}
                </InfoContainer>
            </Container>
        </IndexContentContainer>
    )
}

const Container = styled.a.attrs({
    className: 'row',
})`
    width: 100%;
    align-items: center;
    padding: 0;
    margin: 0;
    z-index: 0;

    &:hover {
        color: inherit;
    }
`

const CoverContainer = styled.div.attrs({
    className: 'col-12 col-md-4',
})`
    display: flex;
    align-items: center;
    justify-content: center;
`

const Cover = styled.img.attrs<{ src: any }>({
    className: 'rounded border',
})`
    max-height: 250px;
`

const InfoContainer = styled.div.attrs({
    className: 'col-12 col-md-8',
})`
    display: flex;
    flex-direction: column;
    text-align: center;
`

const Title = styled.h1`
    font-size: 1.2rem;
    color: var(--text-color);
`

const BookPageContainer = styled.div`
    text-align: center;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
`

const MenuContainer = styled.div`
    position: absolute;
    right: 15px;
    top: 15px;
    z-index: 10;
`

const ButtonContainerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`

const ButtonContainer = styled.div`
    max-width: 500px;
    width: 100%;
`

export default BookDetailInfoCard
